import { DISMISSED_DESKTOP_NOTIFICATIONS_BANNER } from 'Constants/localstorage';
import localforage from 'localforage';
import { MobXProviderContext } from 'mobx-react';
import React, { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router';
import { TDismissDesktopNotifications } from 'Stores/NotificationStore.types';
import { RootStoreProps } from 'Stores/RootStore.types';
import { DesktopNotificationsBannerWrapperProps } from '../DesktopNotificationsBannerWrapper/index.types';

const MS_TO_DAY_MULTIPLIER = 86400000; // 1000 * 60 * 60 * 24
const MAX_DISMISS_COUNT = 1; // Number of times the banner can be dismissed before being hidden permanently

const useShowDesktopNotificationsBanner = ():
  | {
      action: DesktopNotificationsBannerWrapperProps['action'];
      handleClose: () => void;
    }
  | undefined => {
  const { notification: notificationStore, person: personStore } =
    React.useContext<RootStoreProps>(MobXProviderContext);

  const location = useLocation();

  const [isOpen, setIsOpen] = React.useState(false);

  const handleClose = useCallback(() => {
    const updateDismissedInfo = async () => {
      const localForageKey = `${DISMISSED_DESKTOP_NOTIFICATIONS_BANNER}_${personStore.loggedInPersonId}`;

      const dismissedDesktopNotifications =
        await localforage.getItem<TDismissDesktopNotifications>(localForageKey);

      const now = new Date().toISOString();

      // If no dismissal info exists, initialize it
      if (!dismissedDesktopNotifications) {
        await localforage.setItem<TDismissDesktopNotifications>(
          localForageKey,
          {
            lastTimeDismissed: now,
            timesDismissed: 1,
            dismissedPermanently: false,
          }
        );
        return;
      }

      const { timesDismissed } = dismissedDesktopNotifications;

      // Update dismissal info and mark as permanently dismissed if necessary
      await localforage.setItem<TDismissDesktopNotifications>(localForageKey, {
        lastTimeDismissed: now,
        timesDismissed: timesDismissed + 1,
        dismissedPermanently: timesDismissed + 1 >= MAX_DISMISS_COUNT,
      });
    };

    void updateDismissedInfo();
    setIsOpen(false);
  }, [personStore.loggedInPersonId]);

  useEffect(() => {
    const checkBannerVisibility = async () => {
      // If notifications are enabled, or user isn't logged in, don't show the banner
      if (
        notificationStore.webNotificationsEnabled ||
        !personStore.IsLoggedIn
      ) {
        setIsOpen(false);
        return;
      }

      const dismissedDesktopNotifications =
        await localforage.getItem<TDismissDesktopNotifications>(
          `${DISMISSED_DESKTOP_NOTIFICATIONS_BANNER}_${personStore.loggedInPersonId}`
        );

      // Show the banner if no dismissal info exists
      if (!dismissedDesktopNotifications) {
        setIsOpen(true);
        return;
      }

      const { dismissedPermanently, lastTimeDismissed } =
        dismissedDesktopNotifications;

      const thirtyDaysAgo = Date.now() - MS_TO_DAY_MULTIPLIER * 30;

      // Show the banner only if it hasn't been dismissed permanently and was dismissed more than 30 days ago
      setIsOpen(
        !dismissedPermanently &&
          new Date(lastTimeDismissed).valueOf() < thirtyDaysAgo
      );
    };

    void checkBannerVisibility();
  }, [notificationStore.webNotificationsEnabled, personStore.loggedInPersonId]);

  if (!isOpen) {
    return;
  }

  const isSettingsPage = location.pathname.startsWith(
    '/settings/notifications'
  );

  return {
    action: isSettingsPage ? 'enable_notifications' : 'open_settings',
    handleClose,
  };
};

export default useShowDesktopNotificationsBanner;
